import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { NavItem } from '../../types';
import styles from './MobileMenu.module.css';

interface MobileMenuProps {
  navigationData: NavItem[];
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ navigationData, isOpen, onClose }) => {
  const [activeNavId, setActiveNavId] = useState<string | null>(null);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close menu on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const toggleSubmenu = (navId: string) => {
    setActiveNavId(activeNavId === navId ? null : navId);
  };

  const handleLinkClick = () => {
    onClose();
    setActiveNavId(null);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`${styles.backdrop} ${isOpen ? styles.show : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Mobile Menu */}
      <nav
        className={`${styles.mobileMenu} ${isOpen ? styles.open : ''}`}
        aria-label="모바일 네비게이션"
      >
        <div className={styles.menuHeader}>
          <h2 className={styles.menuTitle}>메뉴</h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="메뉴 닫기"
          >
            <span className={styles.closeIcon}>&times;</span>
          </button>
        </div>

        <ul className={styles.navList}>
          {navigationData.map((navItem) => (
            <li key={navItem.id} className={styles.navItem}>
              <div className={styles.navItemHeader}>
                <Link
                  to={navItem.href}
                  className={styles.navLink}
                  onClick={handleLinkClick}
                >
                  {navItem.label}
                </Link>
                {navItem.submenu && navItem.submenu.length > 0 && (
                  <button
                    className={`${styles.toggleButton} ${activeNavId === navItem.id ? styles.active : ''}`}
                    onClick={() => toggleSubmenu(navItem.id)}
                    aria-expanded={activeNavId === navItem.id}
                    aria-label={`${navItem.label} 하위메뉴 ${activeNavId === navItem.id ? '닫기' : '열기'}`}
                  >
                    <span className={styles.chevron}></span>
                  </button>
                )}
              </div>

              {navItem.submenu && navItem.submenu.length > 0 && (
                <ul
                  className={`${styles.submenu} ${activeNavId === navItem.id ? styles.expanded : ''}`}
                >
                  {navItem.submenu.map((subItem) => (
                    <li key={subItem.id} className={styles.submenuItem}>
                      {subItem.isExternal ? (
                        <a
                          href={subItem.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.submenuLink}
                          onClick={handleLinkClick}
                        >
                          {subItem.label}
                          {subItem.icon && (
                            <>
                              &nbsp;
                              <img
                                src={subItem.icon}
                                alt=""
                                style={{ verticalAlign: 'middle' }}
                              />
                            </>
                          )}
                        </a>
                      ) : (
                        <Link
                          to={subItem.href}
                          className={styles.submenuLink}
                          onClick={handleLinkClick}
                        >
                          {subItem.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
};

export default MobileMenu;
